import { CheckSquare } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useEffect, useRef } from "react";
import { formatCEU } from "@/lib/utils";

interface StateRequirementsCardProps {
  newCourse: any;
  setNewCourse: (course: any) => void;
  updateTotalHours?: (hours: number) => void;
}

export const StateRequirementsCard = ({
  newCourse,
  setNewCourse,
  updateTotalHours
}: StateRequirementsCardProps) => {
  const [totalHours, setTotalHours] = useState(0);
  const initialRequirementsSetRef = useRef(false);

  const categories = [
    {
      title: "Positive Behavioral Intervention Strategies",
      key: "behavioralIntervention",
      description: "Training focused on positive behavioral interventions and supports"
    },
    {
      title: "Reading Preparation",
      key: "readingPreparation",
      description: "Comprehensive reading preparation instruction"
    },
    {
      title: "Key Warning Signs for Early-Onset Mental Illness in Children and Adolescents",
      key: "keyWarningSigns",
      description: "Identifying early warning signs of mental health issues"
    },
    {
      title: "Suicide Prevention",
      key: "suicidePrevention",
      description: "Strategies for suicide awareness and prevention"
    },
    {
      title: "English Learners",
      key: "englishLearners",
      description: "Supporting English language learners"
    },
    {
      title: "American Indian History and Culture",
      key: "americanIndianHistory",
      description: "Education on American Indian history, language, and culture"
    }
  ];

  const culturalCompetencySubcategories = [
    {
      title: "Racial, Cultural, and Socioeconomic Groups",
      key: "racial"
    },
    {
      title: "Religion",
      key: "religion"
    },
    {
      title: "Systemic Racism",
      key: "systemicRacism"
    },
    {
      title: "Gender Identity, Including Transgender Students",
      key: "genderIdentity"
    },
    {
      title: "Sexual Orientation",
      key: "sexualOrientation"
    },
    {
      title: "Language Diversity",
      key: "languageDiversity"
    },
    {
      title: "Individuals with Disabilities and Mental Health Concerns",
      key: "disabilities"
    }
  ];

  useEffect(() => {
    if (!newCourse?.state_requirements || initialRequirementsSetRef.current) {
      return;
    }
    
    let updated = false;
    const updatedRequirements = { ...newCourse.state_requirements };
    
    categories.forEach(category => {
      if (updatedRequirements[category.key] && updatedRequirements[`${category.key}Hours`] === undefined) {
        updatedRequirements[`${category.key}Hours`] = 0;
        updated = true;
      }
    });
    
    if (updatedRequirements.culturalCompetency?.enabled) {
      if (updatedRequirements.culturalCompetency.enabledHours === undefined) {
        updatedRequirements.culturalCompetency.enabledHours = 0;
        updated = true;
      }
    }
    
    if (updated) {
      initialRequirementsSetRef.current = true;
      setNewCourse({
        ...newCourse,
        state_requirements: updatedRequirements
      });
    } else {
      initialRequirementsSetRef.current = true;
    }
  }, [newCourse?.state_requirements]);

  useEffect(() => {
    if (!newCourse?.state_requirements) return;
    
    const calculateTotalHours = () => {
      let total = 0;
      
      categories.forEach(category => {
        if (newCourse.state_requirements[category.key]) {
          const hours = parseFloat(newCourse.state_requirements[`${category.key}Hours`] || 0);
          total += isNaN(hours) ? 0 : hours;
        }
      });
      
      if (newCourse.state_requirements.culturalCompetency?.enabled) {
        const mainHours = parseFloat(newCourse.state_requirements.culturalCompetency.enabledHours || 0);
        total += isNaN(mainHours) ? 0 : mainHours;
      }
      
      return total;
    };
    
    const total = calculateTotalHours();
    setTotalHours(total);
    
    if (updateTotalHours) {
      updateTotalHours(total);
    }
  }, [newCourse?.state_requirements, updateTotalHours]);

  const handleRequirementChange = (key: string, checked: boolean) => {
    setNewCourse({
      ...newCourse,
      state_requirements: {
        ...newCourse.state_requirements,
        [key]: checked,
        ...(checked && { [`${key}Hours`]: newCourse.state_requirements[`${key}Hours`] || 0 })
      }
    });
  };

  const handleRequirementHoursChange = (key: string, hours: string) => {
    const numericHours = hours === '' ? '' : parseFloat(hours);
    
    setNewCourse({
      ...newCourse,
      state_requirements: {
        ...newCourse.state_requirements,
        [`${key}Hours`]: numericHours
      }
    });
  };

  const handleCulturalCompetencyChange = (subkey: string, checked: boolean) => {
    setNewCourse({
      ...newCourse,
      state_requirements: {
        ...newCourse.state_requirements,
        culturalCompetency: {
          ...newCourse.state_requirements.culturalCompetency,
          [subkey]: checked
        }
      }
    });
  };

  const handleCulturalCompetencyEnabledChange = (checked: boolean) => {
    setNewCourse({
      ...newCourse,
      state_requirements: {
        ...newCourse.state_requirements,
        culturalCompetency: {
          ...newCourse.state_requirements.culturalCompetency,
          enabled: checked,
          ...(checked && { enabledHours: newCourse.state_requirements.culturalCompetency.enabledHours || 0 })
        }
      }
    });
  };

  const handleCulturalCompetencyHoursChange = (field: string, hours: string) => {
    const numericHours = hours === '' ? '' : parseFloat(hours);
    
    setNewCourse({
      ...newCourse,
      state_requirements: {
        ...newCourse.state_requirements,
        culturalCompetency: {
          ...newCourse.state_requirements.culturalCompetency,
          [field]: numericHours
        }
      }
    });
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <CheckSquare className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-title font-semibold text-primary">State Requirements</h2>
        </div>
        <div className="text-right">
          <p className="text-sm font-sans text-muted-foreground">Total CEU Hours:</p>
          <p className="text-lg font-title font-semibold">{formatCEU(totalHours)}</p>
        </div>
      </div>
      <div className="space-y-6">
        <div className="space-y-4 rounded-lg">
          <div className="grid grid-cols-1 gap-4">
            {categories.map((category) => (
              <div key={category.key} className="p-3 border rounded-md hover:bg-muted/50 transition-colors">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={category.key}
                    checked={newCourse.state_requirements[category.key]}
                    onChange={(e) => handleRequirementChange(category.key, e.target.checked)}
                    className="h-4 w-4"
                  />
                  <label htmlFor={category.key} className="font-serif font-medium cursor-pointer">{category.title}</label>
                  {newCourse.state_requirements[category.key] && (
                    <Badge variant="outline" className="ml-2 bg-primary/5 font-section text-xs">Selected</Badge>
                  )}
                </div>
                <p className="text-sm font-sans text-muted-foreground mt-1 ml-6">{category.description}</p>
                
                {newCourse.state_requirements[category.key] && (
                  <div className="mt-3 ml-6">
                    <Label htmlFor={`${category.key}-hours`} className="text-sm font-serif">CEU Hours</Label>
                    <div className="flex items-center">
                      <Input
                        id={`${category.key}-hours`}
                        type="number"
                        min="0"
                        step="0.1"
                        className="w-20 mt-1 font-sans"
                        value={newCourse.state_requirements[`${category.key}Hours`] || ''}
                        onChange={(e) => handleRequirementHoursChange(category.key, e.target.value)}
                      />
                      <span className="ml-2 text-sm font-sans text-muted-foreground">hours</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <Separator className="my-4" />

          <div className="space-y-4">
            <div className="p-3 border rounded-md hover:bg-muted/50 transition-colors">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="culturalCompetency"
                  checked={newCourse.state_requirements.culturalCompetency.enabled}
                  onChange={(e) => handleCulturalCompetencyEnabledChange(e.target.checked)}
                  className="h-4 w-4"
                />
                <label htmlFor="culturalCompetency" className="font-serif font-medium cursor-pointer">Cultural Competency (selected criteria covered in the training)</label>
                {newCourse.state_requirements.culturalCompetency.enabled && (
                  <Badge variant="outline" className="ml-2 bg-primary/5 font-section text-xs">Selected</Badge>
                )}
              </div>
              <p className="text-sm font-sans text-muted-foreground mt-1 ml-6">
                Training on cultural competency across various dimensions
              </p>

              {newCourse.state_requirements.culturalCompetency.enabled && (
                <div className="mt-3 ml-6">
                  <Label htmlFor="cultural-competency-hours" className="text-sm font-serif">CEU Hours</Label>
                  <div className="flex items-center">
                    <Input
                      id="cultural-competency-hours"
                      type="number"
                      min="0"
                      step="0.1"
                      className="w-20 mt-1 font-sans"
                      value={newCourse.state_requirements.culturalCompetency.enabledHours || ''}
                      onChange={(e) => handleCulturalCompetencyHoursChange('enabledHours', e.target.value)}
                    />
                    <span className="ml-2 text-sm font-sans text-muted-foreground">hours</span>
                  </div>
                </div>
              )}
            </div>

            {newCourse.state_requirements.culturalCompetency.enabled && (
              <div className="ml-6 grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-3 border border-dashed rounded-md bg-muted/20">
                <h4 className="col-span-1 md:col-span-2 text-sm font-serif font-medium mb-1">Select applicable areas:</h4>
                {culturalCompetencySubcategories.map((subcategory) => (
                  <div key={subcategory.key} className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`cultural-${subcategory.key}`}
                        checked={newCourse.state_requirements.culturalCompetency[subcategory.key]}
                        onChange={(e) => handleCulturalCompetencyChange(subcategory.key, e.target.checked)}
                        className="h-4 w-4"
                      />
                      <label htmlFor={`cultural-${subcategory.key}`} className="text-sm font-sans cursor-pointer">{subcategory.title}</label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
